import { Box, Typography } from "@mui/material";
import moment from "moment";
import numWords from "num-words";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { invoiceValidationRules } from "../../pages/client/validation";
import { CompanyService, CRMService, ProjectService, AccountService } from "../../services";
import { get_date } from "../generalComponets";
import { handlePdfDownload } from "../pdf";
import { ActionButton, InputField, SelectField } from "../v3";
import { GovernmentInvoicePdf, getInvoicePdfPayload } from "./GovernmentInvoicePdf.template";
import { governmentClientInvoiceItemDefaults } from "./constants";
import { markTableReset } from "../../utils/tableStateStorage";

const formatCurrency = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const formatNumberToWords = (number) => {
  if (!number && number !== 0) return "";
  return numWords(Math.round(number));
};

export const AddEditGovernmentInvoiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState({
    amount: undefined,
    date: get_date(Date.now(), "YYYY-MM-DD"),
    invoiceNumber: undefined,
    bankDetails: {
      bankAccountNumber: "",
      ifscCode: "",
    },
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  const clientId = searchParams.get("clientId");
  const projectId = searchParams.get("projectId");

  useEffect(() => {
    if (clientId) fetchClient(clientId);
    if (projectId) fetchProject(projectId);
    if (id) fetchInvoice(id);
  }, [clientId, projectId, id]);

  const fetchClient = async (value) => {
    try {
      const { data } = await CRMService.getClientById(value);
      setSelectedClient(data);
    } catch (error) {
      toast.error("Failed to load client details");
    }
  };

  const fetchProject = async (value) => {
    try {
      const { data } = await ProjectService.getProjectById(value);
      setSelectedProject(data);
    } catch (error) {
      toast.error("Failed to load project details");
    }
  };

  const fetchInvoice = async (invoiceId) => {
    try {
      const { data: invoice } = await CRMService.getClientInvoiceById(invoiceId);

      setSelectedClient(invoice?.client || null);
      setSelectedProject(invoice?.project || null);
      setInvoiceDetails({
        amount: invoice?.subtotal,
        date: get_date(invoice?.date, "YYYY-MM-DD"),
        invoiceNumber: invoice?.invoiceNumber,
        bankDetails: {
          bankAccountNumber: invoice?.bankDetails?.bankAccountNumber ?? "",
          ifscCode: invoice?.bankDetails?.ifscCode ?? "",
        },
      });
      const savedAccountNumber = invoice?.bankDetails?.bankAccountNumber;
      const savedIfscCode = invoice?.bankDetails?.ifscCode;

      setSelectedBank(null);

      if (savedAccountNumber && savedIfscCode) {
        try {
          const { data } = await AccountService.getAccounts({
            type: "BANK",
          });

          const accounts = data?.rows ?? [];

          const matchingBank = accounts.find(
            (account) =>
              String(account.meta?.accountNumber ?? "").trim() === String(savedAccountNumber).trim() &&
              String(account.meta?.ifscCode ?? "")
                .trim()
                .toUpperCase() === String(savedIfscCode).trim().toUpperCase(),
          );

          setSelectedBank(matchingBank ?? null);
        } catch (error) {
          console.error("Failed to load bank accounts", error);
        }
      }
    } catch (error) {
      toast.error("Failed to load invoice details");
    }
  };

  const derivedValues = useMemo(() => {
    const amount = Number(invoiceDetails.amount);
    const totalTax = (amount * governmentClientInvoiceItemDefaults.taxRate) / 100;
    const cgstAmount = totalTax / 2;
    const sgstAmount = totalTax / 2;
    const totalAmount = amount + totalTax;
    // As per Section 170 of CGST Act, 2017
    const grandTotal = Math.round(totalAmount);

    return {
      cgstAmount,
      sgstAmount,
      amount,
      totalAmount,
      grandTotal,
    };
  }, [invoiceDetails]);

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setInvoiceDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleBankChange = (bank) => {
    setSelectedBank(bank);

    setInvoiceDetails((prev) => ({
      ...prev,
      bankDetails: {
        bankAccountNumber: bank?.meta?.accountNumber ?? "",
        ifscCode: bank?.meta?.ifscCode ?? "",
      },
    }));
    setErrors((prev) => ({
      ...prev,
      bankAccountNumber: "",
      ifscCode: "",
    }));
  };

  const handleBankDetailsChange = (event) => {
    const { name, value } = event.target;

    setInvoiceDetails((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [name]: value,
      },
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const errors = invoiceValidationRules(selectedClient?.id, selectedProject?.id, invoiceDetails);
    setErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const accountNumber = invoiceDetails.bankDetails?.bankAccountNumber?.trim();
    const ifscCode = invoiceDetails.bankDetails?.ifscCode?.trim();

    if (ifscCode && !accountNumber) {
      setErrors({
        bankAccountNumber: "Account Number is required when IFSC Code is provided",
      });
      return;
    }

    if (accountNumber && !ifscCode) {
      setErrors({
        ifscCode: "IFSC Code is required when Account Number is provided",
      });
      return;
    }

    const payload = {
      projectId: selectedProject?.id,
      date: moment(`${invoiceDetails.date} ${moment().format("HH:mm:ss")}`).toISOString(),
      invoiceNumber: invoiceDetails.invoiceNumber,
      isTaxIncluded: true,
      type: governmentClientInvoiceItemDefaults.type,
      amount: Number(invoiceDetails.amount),
      taxDetails: {
        sgstPercentage: governmentClientInvoiceItemDefaults.taxRate / 2,
        sgstAmount: derivedValues.sgstAmount,
        cgstPercentage: governmentClientInvoiceItemDefaults.taxRate / 2,
        cgstAmount: derivedValues.cgstAmount,
        igstPercentage: 0, // TODO: TBD
        igstAmount: 0, // TODO: TBD
      },
      bankDetails: {
        bankAccountNumber: invoiceDetails.bankDetails?.bankAccountNumber ?? "",
        ifscCode: invoiceDetails.bankDetails?.ifscCode ?? "",
      },
    };

    try {
      setSaving(true);
      if (id) {
        await CRMService.updateClientInvoice(id, payload);
        toast.success("Invoice updated successfully");
      } else {
        const { data: createdInvoice } = await CRMService.createClientInvoice(selectedClient.id, payload);

        // for downloading pdf right after creating invoice
        const { data: invoice } = await CRMService.getClientInvoiceById(createdInvoice.id);
        const { data: project } = await ProjectService.getProjectById(selectedProject.id);
        const { data: client } = await CRMService.getClientById(invoice?.clientId);
        const { data: company } = await CompanyService.getCompanyDetails(invoice?.companyId);

        const pdfData = getInvoicePdfPayload(invoice, project, client, company);
        const pdfDocument = <GovernmentInvoicePdf data={pdfData} />;
        await handlePdfDownload({
          document: pdfDocument,
          pdfTitle: `Client Invoice-${invoice?.invoiceNumber}`,
        });
        toast.success("Invoice created successfully");
        // The list opens fresh, so its filters don't hide the new invoice.
        markTableReset();
      }
      navigate(-1);
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(Array.isArray(message) ? message.join(", ") : message || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box className="fl gap-2">
        <InputField
          required
          label="Date"
          name="date"
          type="date"
          value={invoiceDetails.date}
          onChange={updateDetailsState}
          error={!!errors.date}
          helperText={errors.date}
          fullWidth
        />
        <InputField
          required
          label="Amount"
          name="amount"
          type="number"
          value={invoiceDetails.amount}
          onChange={updateDetailsState}
          error={!!errors.amount}
          helperText={errors.amount}
          fullWidth
        />
        <InputField
          label="Invoice Number"
          name="invoiceNumber"
          placeholder="Eg: CI-00001"
          value={invoiceDetails.invoiceNumber}
          onChange={updateDetailsState}
          error={!!errors.invoiceNumber}
          helperText={errors.invoiceNumber}
          fullWidth
        />
      </Box>

      <Box
        sx={{
          mt: 2,
          p: 2.5,
          border: "1px solid #E5E7EB",
          borderRadius: 2,
          backgroundColor: "#F9FAFB",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          Bank Details
        </Typography>

        <Box sx={{ mb: 2 }}>
          <SelectField
            label="Select Bank Account"
            value={selectedBank}
            getData={AccountService.getAccounts}
            getDataStaticFilters={{ type: "BANK" }}
            optionFilter={(accounts) => accounts.filter((account) => account.type === "BANK")}
            optionValueFields={["name"]}
            onChange={handleBankChange}
            style={{ width: "100%" }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
            gap: 2,
          }}
        >
          <InputField
            label="Bank Account Number"
            name="bankAccountNumber"
            type="text"
            value={invoiceDetails.bankDetails?.bankAccountNumber ?? ""}
            onChange={handleBankDetailsChange}
            error={!!errors.bankAccountNumber}
            helperText={errors.bankAccountNumber}
            fullWidth
          />

          <InputField
            label="IFSC Code"
            name="ifscCode"
            type="text"
            value={invoiceDetails.bankDetails?.ifscCode ?? ""}
            onChange={handleBankDetailsChange}
            error={!!errors.ifscCode}
            helperText={errors.ifscCode}
            fullWidth
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          p: 2,
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          backgroundColor: "#F9FAFB",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Calculated Summary
        </Typography>
        <Typography variant="body2">Amount: ₹ {formatCurrency(derivedValues.amount)}</Typography>
        <Typography variant="body2">CGST: ₹ {formatCurrency(derivedValues.cgstAmount)}</Typography>
        <Typography variant="body2">SGST: ₹ {formatCurrency(derivedValues.sgstAmount)}</Typography>
        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
          Grand Total: ₹ {formatCurrency(derivedValues.grandTotal)}
        </Typography>

        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
          Amount in Words: {`Rupees ${formatNumberToWords(derivedValues.grandTotal)} only`}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <ActionButton buttonText="Cancel" variant="outlined" onButtonClick={() => navigate(-1)} />
        <ActionButton buttonText={id ? "Update Invoice" : "Create Invoice"} onButtonClick={handleSubmit} loading={saving} />
      </Box>
    </Box>
  );
};
